import { useState, useEffect } from 'react';
import BirthdayBanner from '@/components/BirthdayBanner';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { useReceptionAccess } from '@/lib/receptionAccess';
import { toast } from 'sonner';
import { 
  Calendar, 
  Camera, 
  ClipboardCheck, 
  ClipboardList, 
  MessageSquare, 
  FolderKanban,
  CheckCircle2,
  Clock,
  ArrowRight,
  User,
  Pencil,
  X,
  Users,
  Trash2,
  KeyRound,
  Copy,
  Check,
  Wrench,
  Cake,
  Droplets,
  Boxes,
  TrendingUp
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import SalaryCard from '@/components/SalaryCard';
import ModuleBadge from '@/components/ModuleBadge';
import { useModuleBadges } from '@/lib/moduleBadges';
import { computeAge, formatAge } from '@/lib/age';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPage() {
  const { user, isPreviewMode } = useAuth();
  const { t, language } = useLanguage();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingMember, setEditingMember] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', role: '', email: '', company: '', birthday: '', daily_rate: '' });
  const [checkingIn, setCheckingIn] = useState(false);
  const navigate = useNavigate();
  const [resetPasswordDialog, setResetPasswordDialog] = useState(null);
  const [copiedPassword, setCopiedPassword] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null); // For custom confirm dialogs
  const [photoPreview, setPhotoPreview] = useState(null); // { url, name } fullscreen avatar viewer
  const moduleBadges = useModuleBadges(); // pastilles rouges de non-lus par module

  // The boss does not check in himself: hide the whole pointage module for him
  const isManagerView = user?.role === 'manager' && !isPreviewMode;
  const receptionAccess = useReceptionAccess(user?.role === 'manager');

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    // Trigger maintenance reminders + project alerts checks on dashboard load
    axios.post(`${API}/equipment/check-maintenance-reminders`).catch(() => {});
    axios.post(`${API}/projects/check-alerts`).catch(() => {});
  }, []);

  // Pointage depuis l'accueil : on n'envoie PLUS de pointage d'ici. Le chantier
  // est obligatoire (ordre Pavel 14/09/2026) et ce bouton partait sans chantier :
  // le serveur refusait (400) et l'ecran affichait quand meme "deja pointe"
  // (incident Sergei, 29/09/2026). Le bouton ouvre la page Pointage, qui
  // demande le chantier et enregistre reellement.
  const handleCheckin = () => {
    if (stats?.checked_in_today || checkingIn) return;
    navigate('/attendance?pointer=1');
  };

  const handleEditMember = (member) => {
    setEditingMember(member);
    // Convert old MM-DD format to YYYY-MM-DD for the date input
    let birthdayValue = member.birthday || '';
    if (birthdayValue && birthdayValue.length === 5 && /^\d{2}-\d{2}$/.test(birthdayValue)) {
      // Old MM-DD format: convert to 2000-MM-DD for the date picker
      birthdayValue = `2000-${birthdayValue}`;
    }
    setEditForm({ name: member.name || '', role: member.role || '', email: member.email || '', company: member.company || 'La Piscine Container', birthday: birthdayValue, daily_rate: member.daily_rate || '' });
  };

  const handleDeleteMember = (member) => {
    if (member.role === 'manager') {
      toast.error(t('cannotDeleteManager'));
      return;
    }
    
    // Show custom confirm dialog
    setConfirmDialog({
      type: 'delete',
      member: member,
      title: { fr: 'Supprimer cet employé ?', ru: 'Удалить сотрудника?', md: 'Ștergeți angajatul?', uk: 'Видалити працівника?' }[language] || 'Supprimer cet employé ?',
      message: { fr: `Voulez-vous vraiment supprimer ${member.name} ?`, ru: `Удалить ${member.name}?`, md: `Ștergeți ${member.name}?`, uk: `Видалити ${member.name}?` }[language] || `Voulez-vous vraiment supprimer ${member.name} ?`
    });
  };

  const confirmDeleteMember = async () => {
    if (!confirmDialog?.member) return;
    const member = confirmDialog.member;
    setConfirmDialog(null);
    
    try {
      await axios.delete(`${API}/users/${member.id}`);
      setStats({
        ...stats,
        team_members: stats.team_members.filter(m => m.id !== member.id)
      });
      toast.success(t('employeeUpdated'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const handleResetPassword = (member) => {
    // Show custom confirm dialog
    setConfirmDialog({
      type: 'resetPassword',
      member: member,
      title: { fr: 'Réinitialiser le mot de passe ?', ru: 'Сбросить пароль?', md: 'Resetați parola?', uk: 'Скинути пароль?' }[language] || 'Réinitialiser le mot de passe ?',
      message: { fr: `Réinitialiser le mot de passe de ${member.name} ?`, ru: `Сбросить пароль для ${member.name}?`, md: `Resetați parola pentru ${member.name}?`, uk: `Скинути пароль для ${member.name}?` }[language] || `Réinitialiser le mot de passe de ${member.name} ?`
    });
  };

  const confirmResetPassword = async () => {
    if (!confirmDialog?.member) return;
    const member = confirmDialog.member;
    setConfirmDialog(null);
    
    try {
      const response = await axios.post(`${API}/users/${member.id}/reset-password`);
      setResetPasswordDialog({
        userName: response.data.user_name,
        userEmail: response.data.user_email,
        temporaryPassword: response.data.temporary_password
      });
      setCopiedPassword(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedPassword(true);
      setTimeout(() => setCopiedPassword(false), 2000);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedPassword(true);
      setTimeout(() => setCopiedPassword(false), 2000);
    }
  };

  const handleSaveMember = async () => {
    try {
      const isEditingSelf = editingMember?.id === user?.id;
      const parsedDailyRate = editForm.daily_rate !== '' && editForm.daily_rate !== null && editForm.daily_rate !== undefined
        ? parseFloat(editForm.daily_rate) || null
        : null;
      if (isEditingSelf) {
        // Self-edit: use admin-update (name, role only)
        await axios.put(`${API}/users/${editingMember.id}/admin-update`, { name: editForm.name, role: editForm.role });
      } else {
        // Manager editing another user: use manager-update (name, email, company, birthday, daily_rate)
        await axios.put(`${API}/users/${editingMember.id}/manager-update`, { 
          name: editForm.name, 
          email: editForm.email, 
          company: editForm.company,
          birthday: editForm.birthday || null,
          daily_rate: parsedDailyRate
        });
      }
      const payload = isEditingSelf 
        ? { name: editForm.name, role: editForm.role }
        : { name: editForm.name, email: editForm.email, company: editForm.company, birthday: editForm.birthday, daily_rate: parsedDailyRate };
      setStats({
        ...stats,
        team_members: stats.team_members.map(m => 
          m.id === editingMember.id ? { ...m, ...payload } : m
        )
      });
      setEditingMember(null);
      toast.success(t('employeeUpdated'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const today = new Date().toLocaleDateString(
    language === 'ru' ? 'ru-RU' : language === 'md' ? 'ro-RO' : language === 'uk' ? 'uk-UA' : 'fr-FR',
    { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }
  );

  if (loading) {
    return (
      <div className="space-y-6 animate-fadeIn" data-testid="dashboard-skeleton">
        <div className="card-base p-6 h-32 bg-slate-200/70 animate-pulse" />
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="card-base h-24 bg-slate-200/70 animate-pulse" />
          ))}
        </div>
        <div className="grid grid-cols-2 gap-4">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="card-base h-28 bg-slate-200/70 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page" className="space-y-6 animate-fadeIn">
      <BirthdayBanner />
      {/* Welcome Section with Avatar */}
      <div className="card-base p-6 bg-gradient-to-r from-slate-900 to-slate-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Avatar */}
            <div
              className={`w-16 h-16 rounded-full overflow-hidden border-2 border-white/20 shadow-lg flex-shrink-0 ${user?.photo_url ? 'cursor-pointer active:scale-95 transition-transform' : ''}`}
              onClick={() => user?.photo_url && setPhotoPreview({ url: user.photo_url, name: user.name })}
            >
              {user?.photo_url ? (
                <img src={user.photo_url} alt={user.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white text-2xl font-bold">
                  {user?.name?.charAt(0)?.toUpperCase() || '?'}
                </div>
              )}
            </div>
            <div>
              <p className="text-slate-400 text-sm uppercase tracking-wide font-semibold mb-1">{today}</p>
              <h1 className="text-2xl md:text-3xl font-bold text-white tracking-tight uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                {t('hello')}, {user?.name?.split(' ')[0]} !
              </h1>
            </div>
          </div>
          {!isManagerView && (
          <button
            onClick={handleCheckin}
            disabled={stats?.checked_in_today || checkingIn}
            className={`w-12 h-12 rounded-sm flex items-center justify-center transition-all ${
              stats?.checked_in_today 
                ? 'bg-emerald-500 cursor-default' 
                : 'bg-amber-500 cursor-pointer hover:bg-amber-600 hover:scale-110 active:scale-95'
            }`}
            data-testid="dashboard-checkin-btn"
            title={!stats?.checked_in_today ? ({ fr: 'Cliquez pour pointer', ru: 'Нажмите для отметки', md: 'Apăsați pentru a puncta', uk: 'Натисніть для відмітки' }[language] || 'Cliquez pour pointer') : ''}
          >
            {checkingIn ? (
              <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : stats?.checked_in_today ? (
              <CheckCircle2 className="w-6 h-6 text-white" />
            ) : (
              <Clock className="w-6 h-6 text-slate-900" />
            )}
          </button>
          )}
        </div>
        {isManagerView ? null : stats?.checked_in_today ? (
          <p className="text-emerald-400 mt-3 ml-20 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
            {t('checkedInToday')}
          </p>
        ) : (
          <button
            onClick={handleCheckin}
            disabled={checkingIn}
            className="w-full mt-4 py-3 px-4 rounded-md bg-amber-500 hover:bg-amber-600 active:scale-[0.98] transition-all text-slate-900 font-bold flex items-center justify-center gap-2 shadow-lg"
            data-testid="dashboard-checkin-banner"
          >
            {checkingIn ? (
              <div className="w-5 h-5 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
            ) : (
              <Clock className="w-5 h-5" />
            )}
            {{ fr: 'Pointer maintenant', ru: 'Отметиться сейчас', md: 'Punctați acum', uk: 'Відмітитись зараз' }[language] || 'Pointer maintenant'}
          </button>
        )}
      </div>

      {/* Salary Card - Employee view or Preview mode */}
      {(user?.role !== 'manager' || isPreviewMode) && <SalaryCard compact={true} />}

      {/* Quick Access Buttons - All in one grid. Messages en premier (Pavel 2026-08-02) */}
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-4">
        <Link
          to="/chat"
          className="relative card-base p-4 flex flex-col items-center justify-center gap-2 hover:scale-105 active:scale-95 transition-transform cursor-pointer bg-gradient-to-br from-cyan-50 to-cyan-100 border-cyan-200"
          data-testid="quick-access-chat"
        >
          <ModuleBadge count={moduleBadges.chat} className="top-2 right-2" />
          <div className="w-12 h-12 bg-cyan-500 rounded-xl flex items-center justify-center">
            <MessageSquare className="w-6 h-6 text-white" />
          </div>
          <span className="font-semibold text-cyan-700 text-sm text-center">
            {{ fr: 'Messages', ru: 'Сообщения', md: 'Mesaje', uk: 'Повідомлення' }[language] || 'Messages'}
          </span>
        </Link>

        {!isManagerView && (
        <Link
          to="/attendance"
          className="relative card-base p-4 flex flex-col items-center justify-center gap-2 hover:scale-105 active:scale-95 transition-transform cursor-pointer bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200"
          data-testid="quick-access-attendance"
        >
          <ModuleBadge count={moduleBadges.attendance} className="top-2 right-2" />
          <div className="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center">
            <Calendar className="w-6 h-6 text-white" />
          </div>
          <span className="font-semibold text-emerald-700 text-sm text-center">
            {t('attendance')}
          </span>
        </Link>
        )}

        <Link
          to="/projects"
          className="relative card-base p-4 flex flex-col items-center justify-center gap-2 hover:scale-105 active:scale-95 transition-transform cursor-pointer bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200"
          data-testid="quick-access-projects"
        >
          <ModuleBadge count={moduleBadges.projects} className="top-2 right-2" />
          <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
            <FolderKanban className="w-6 h-6 text-white" />
          </div>
          <span className="font-semibold text-blue-700 text-sm text-center">
            {t('projects')}
          </span>
        </Link>

        <Link
          to="/materials"
          className="relative card-base p-4 flex flex-col items-center justify-center gap-2 hover:scale-105 active:scale-95 transition-transform cursor-pointer bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200"
          data-testid="quick-access-materials"
        >
          <ModuleBadge count={moduleBadges.materials} className="top-2 right-2" />
          <div className="w-12 h-12 bg-amber-500 rounded-xl flex items-center justify-center">
            <ClipboardList className="w-6 h-6 text-white" />
          </div>
          <span className="font-semibold text-amber-700 text-sm text-center">
            {t('materials')}
          </span>
        </Link>

        {/* Manager only: Team Attendance */}
        {user?.role === 'manager' && (
          <Link
            to="/team-attendance"
            className="relative card-base p-4 flex flex-col items-center justify-center gap-2 hover:scale-105 active:scale-95 transition-transform cursor-pointer bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200"
            data-testid="quick-access-team-attendance"
          >
            <ModuleBadge count={moduleBadges.team} className="top-2 right-2" />
            <div className="w-12 h-12 bg-indigo-500 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-white" />
            </div>
            <span className="font-semibold text-indigo-700 text-sm text-center">
              {t('team')}
            </span>
          </Link>
        )}

        {receptionAccess && (
        <Link
          to="/reception"
          className="relative card-base p-4 flex flex-col items-center justify-center gap-2 hover:scale-105 active:scale-95 transition-transform cursor-pointer bg-gradient-to-br from-teal-50 to-teal-100 border-teal-200"
          data-testid="quick-access-reception"
        >
          <ModuleBadge count={moduleBadges.reception} className="top-2 right-2" />
          <div className="w-12 h-12 bg-teal-500 rounded-xl flex items-center justify-center">
            <ClipboardCheck className="w-6 h-6 text-white" />
          </div>
          <span className="font-semibold text-teal-700 text-sm text-center">
            {({ fr: 'Réception', ru: 'Приёмка', md: 'Recepție', uk: 'Приймання' })[language] || 'Réception'}
          </span>
        </Link>
        )}

        <Link
          to="/photos"
          className="relative card-base p-4 flex flex-col items-center justify-center gap-2 hover:scale-105 active:scale-95 transition-transform cursor-pointer bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200"
          data-testid="quick-access-add-photo"
        >
          <ModuleBadge count={moduleBadges.photos} className="top-2 right-2" />
          <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center">
            <Camera className="w-6 h-6 text-white" />
          </div>
          <span className="font-semibold text-purple-700 text-sm text-center">
            {t('photos')}
          </span>
        </Link>

        <Link
          to="/equipment"
          className="relative card-base p-4 flex flex-col items-center justify-center gap-2 hover:scale-105 active:scale-95 transition-transform cursor-pointer bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200"
          data-testid="quick-access-equipment"
        >
          <ModuleBadge count={moduleBadges.equipment} className="top-2 right-2" />
          <div className="w-12 h-12 bg-slate-600 rounded-xl flex items-center justify-center">
            <Wrench className="w-6 h-6 text-white" />
          </div>
          <span className="font-semibold text-slate-700 text-sm text-center">
            {{ fr: 'Entretien voitures', ru: 'Обслуживание автомобилей', md: 'Întreținere mașini', uk: 'Обслуговування автомобілів' }[language] || 'Entretien voitures'}
          </span>
        </Link>

        <Link
          to="/tools"
          className="card-base p-4 flex flex-col items-center justify-center gap-2 hover:scale-105 active:scale-95 transition-transform cursor-pointer bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200"
          data-testid="quick-access-tools"
        >
          <div className="w-12 h-12 bg-indigo-600 rounded-xl flex items-center justify-center">
            <Boxes className="w-6 h-6 text-white" />
          </div>
          <span className="font-semibold text-indigo-700 text-sm text-center">
            {{ fr: 'Outils', ru: 'Инструменты', md: 'Unelte', uk: 'Інструменти' }[language] || 'Outils'}
          </span>
        </Link>

        {/* LPC only: Pool Maintenance */}
        {(user?.company === 'La Piscine Container' || user?.role === 'manager') && (
          <Link 
            to="/maintenance" 
            className="card-base p-4 flex flex-col items-center justify-center gap-2 hover:scale-105 active:scale-95 transition-transform cursor-pointer bg-gradient-to-br from-cyan-50 to-cyan-100 border-cyan-200"
            data-testid="quick-access-maintenance"
          >
            <div className="w-12 h-12 bg-cyan-500 rounded-xl flex items-center justify-center">
              <Droplets className="w-6 h-6 text-white" />
            </div>
            <span className="font-semibold text-cyan-700 text-sm text-center">
              {{ fr: 'Entretien piscines', ru: 'Обслуживание бассейнов', md: 'Întreținere piscine', uk: 'Обслуговування басейнів' }[language] || 'Entretien piscines'}
            </span>
          </Link>
        )}

        {/* Manager : Statistiques / rentabilite par piscine */}
        {user?.role === 'manager' && (
          <Link
            to="/stats"
            className="card-base p-4 flex flex-col items-center justify-center gap-2 hover:scale-105 active:scale-95 transition-transform cursor-pointer bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200"
            data-testid="quick-access-stats"
          >
            <div className="w-12 h-12 bg-emerald-600 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <span className="font-semibold text-emerald-700 text-sm text-center">
              {{ fr: 'Statistiques', ru: 'Статистика', md: 'Statistici', uk: 'Статистика' }[language] || 'Statistiques'}
            </span>
          </Link>
        )}
      </div>

      {/* Quick Stats Grid - Clickable */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {!isManagerView && (
        <Link to="/attendance" className="stat-card animate-slideUp stagger-1 hover:scale-105 active:scale-95 transition-transform cursor-pointer" data-testid="stat-attendance">
          <div className="flex items-center gap-2 text-emerald-600">
            <Calendar className="w-5 h-5" />
          </div>
          <span className="stat-value">{stats?.my_attendance_days || 0}</span>
          <span className="stat-label">{t('daysWorked')}</span>
        </Link>
        )}

        <Link to="/projects" className="stat-card animate-slideUp stagger-2 hover:scale-105 active:scale-95 transition-transform cursor-pointer" data-testid="stat-projects">
          <div className="flex items-center gap-2 text-blue-600">
            <FolderKanban className="w-5 h-5" />
          </div>
          <span className="stat-value">{stats?.active_projects || 0}</span>
          <span className="stat-label">{t('activeProjects')}</span>
        </Link>
        
        <Link to="/photos" className="stat-card animate-slideUp stagger-3 hover:scale-105 active:scale-95 transition-transform cursor-pointer" data-testid="stat-photos">
          <div className="flex items-center gap-2 text-purple-600">
            <Camera className="w-5 h-5" />
          </div>
          <span className="stat-value">{stats?.total_photos || 0}</span>
          <span className="stat-label">{t('photosLabel')}</span>
        </Link>
        
        <Link to="/materials" className="stat-card animate-slideUp stagger-4 hover:scale-105 active:scale-95 transition-transform cursor-pointer" data-testid="stat-materials">
          <div className="flex items-center gap-2 text-amber-600">
            <ClipboardList className="w-5 h-5" />
          </div>
          <span className="stat-value">{stats?.pending_materials || 0}</span>
          <span className="stat-label">{t('pendingMaterials')}</span>
        </Link>
      </div>

      {/* Team Section */}
      <div className="card-base p-6">
        <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
          <User className="w-5 h-5" />
          {t('team')}
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {stats?.team_members?.map((member) => (
            <div 
              key={member.id} 
              className="text-center"
            >
              <div
                className={`w-16 h-16 mx-auto rounded-full overflow-hidden bg-gradient-to-br from-slate-200 to-slate-300 border-2 border-white shadow-md relative ${member.id === user?.id ? 'cursor-pointer group' : member.photo_url ? 'cursor-pointer active:scale-95 transition-transform' : ''}`}
                onClick={() => member.id === user?.id ? handleEditMember(member) : member.photo_url && setPhotoPreview({ url: member.photo_url, name: member.name })}
              >
                {member.photo_url ? (
                  <img src={member.photo_url} alt={member.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-slate-500 text-xl font-bold">
                    {member.name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                )}
                {member.id === user?.id && (
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Pencil className="w-5 h-5 text-white" />
                  </div>
                )}
              </div>
              <p className="mt-2 text-sm font-medium text-slate-700 truncate">{member.name}</p>
              <p className="text-xs text-slate-400">{member.role === 'manager' ? t('manager') : member.role === 'subcontractor' ? ({ fr: 'Sous-traitant', ru: 'Субподрядчик', md: 'Subcontractant', uk: 'Субпідрядник' }[language] || 'Sous-traitant') : t('employee')}</p>
              {member.birthday ? (
                <p className="text-xs text-pink-500 flex items-center justify-center gap-1 mt-0.5">
                  <Cake className="w-3 h-3" />
                  {(() => {
                    const b = member.birthday;
                    // Handle YYYY-MM-DD format (full date with year)
                    if (b.length === 10 && /^\d{4}-\d{2}-\d{2}$/.test(b)) {
                      const [y, m, d] = b.split('-');
                      const date = new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
                      const dateStr = date.toLocaleDateString(language === 'fr' ? 'fr-FR' : language === 'ru' ? 'ru-RU' : language === 'md' ? 'ro-RO' : 'uk-UA', { day: 'numeric', month: 'long', year: 'numeric' });
                      const age = computeAge(b);
                      return age !== null ? `${dateStr} (${formatAge(age, language)})` : dateStr;
                    }
                    // Handle old MM-DD format
                    if (b.length === 5 && /^\d{2}-\d{2}$/.test(b)) {
                      const [m, d] = b.split('-');
                      const date = new Date(2000, parseInt(m) - 1, parseInt(d));
                      return date.toLocaleDateString(language === 'fr' ? 'fr-FR' : language === 'ru' ? 'ru-RU' : language === 'md' ? 'ro-RO' : 'uk-UA', { day: 'numeric', month: 'long' });
                    }
                    return b;
                  })()}
                </p>
              ) : (
                <p className="text-xs text-gray-300 italic mt-0.5">
                  {{ fr: 'Anniv. non renseigné', ru: 'Д.р. не указана', md: 'Zi naștere necompletată', uk: 'Д.н. не вказана' }[language] || 'Anniv. non renseigné'}
                </p>
              )}
              {member.email && (
                <p className="text-xs text-slate-400 truncate" title={member.email}>{member.email}</p>
              )}
              {member.id === user?.id && (
                <span className="text-xs text-blue-500 font-medium">(Vous)</span>
              )}
              {/* Action buttons below each employee */}
              {user?.role === 'manager' && member.id !== user?.id && member.role !== 'manager' && (
                <div className="flex items-center justify-center gap-2 mt-3 flex-wrap">
                  <button
                    type="button"
                    onClick={() => handleEditMember(member)}
                    className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 active:bg-blue-200 rounded-lg transition-colors cursor-pointer touch-manipulation"
                    data-testid={`edit-profile-${member.id}`}
                  >
                    <Pencil className="w-4 h-4" />
                    {{ fr: 'Profil', ru: 'Профиль', md: 'Profil', uk: 'Профіль' }[language] || 'Profil'}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleResetPassword(member)}
                    className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 active:bg-amber-200 rounded-lg transition-colors cursor-pointer touch-manipulation"
                    data-testid={`reset-password-${member.id}`}
                  >
                    <KeyRound className="w-4 h-4" />
                    {{ fr: 'MDP', ru: 'Пароль', md: 'Parolă', uk: 'Пароль' }[language] || 'MDP'}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeleteMember(member)}
                    className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100 active:bg-red-200 rounded-lg transition-colors cursor-pointer touch-manipulation"
                    data-testid={`delete-member-${member.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Edit Profile Dialog */}
      <Dialog open={!!editingMember} onOpenChange={(open) => !open && setEditingMember(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              {editingMember?.id === user?.id 
                ? t('editMyProfile') 
                : `${{ fr: 'Modifier', ru: 'Изменить', md: 'Editează', uk: 'Редагувати' }[language] || 'Modifier'} ${editingMember?.name || ''}`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-sm font-medium text-gray-700">
                {t('name')}
              </Label>
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                placeholder={t('yourName')}
                className="mt-1"
              />
            </div>
            {/* Show email and company fields for managers editing other users */}
            {user?.role === 'manager' && editingMember?.id !== user?.id && (
              <>
                <div>
                  <Label className="text-sm font-medium text-gray-700">
                    Email
                  </Label>
                  <Input
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    placeholder="email@exemple.fr"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700">
                    {{ fr: 'Societe', ru: 'Компания', md: 'Companie', uk: 'Компанія' }[language] || 'Societe'}
                  </Label>
                  <select
                    value={editForm.company}
                    onChange={(e) => setEditForm({ ...editForm, company: e.target.value })}
                    className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  >
                    <option value="La Piscine Container">La Piscine Container</option>
                    <option value="Eco Bati Construction">Eco Bati Construction</option>
                  </select>
                </div>
              </>
            )}
            {/* Birthday field - manager editing other users */}
            {user?.role === 'manager' && editingMember?.id !== user?.id && (
              <div>
                <Label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                  <Cake className="w-4 h-4 text-pink-400" />
                  {{ fr: 'Date d\'anniversaire', ru: 'Дата рождения', md: 'Data de naștere', uk: 'Дата народження' }[language] || 'Date d\'anniversaire'}
                </Label>
                <Input
                  type="date"
                  value={editForm.birthday}
                  onChange={(e) => setEditForm({ ...editForm, birthday: e.target.value })}
                  className="mt-1"
                  data-testid="dashboard-birthday-input"
                />
              </div>
            )}
            {/* Daily rate - manager editing other users */}
            {user?.role === 'manager' && editingMember?.id !== user?.id && (
              <div>
                <Label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                  {{ fr: 'Taux journalier (€)', ru: 'Дневная ставка (€)', md: 'Tarif zilnic (€)', uk: 'Денна ставка (€)' }[language] || 'Taux journalier (€)'}
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={editForm.daily_rate}
                  onChange={(e) => setEditForm({ ...editForm, daily_rate: e.target.value })}
                  placeholder="Ex: 120"
                  className="mt-1"
                  data-testid="dashboard-daily-rate-input"
                />
              </div>
            )}
            <div>
              <Label className="text-sm font-medium text-gray-700">                {t('rolePosition')}
              </Label>
              <Input
                value={editForm.role}
                onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                placeholder={t('roleExample')}
                className="mt-1"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button onClick={() => setEditingMember(null)} variant="outline" className="flex-1">
                {t('cancel')}
              </Button>
              <Button onClick={handleSaveMember} className="flex-1 bg-blue-600 hover:bg-blue-700">
                {t('save')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={!!resetPasswordDialog} onOpenChange={(open) => !open && setResetPasswordDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-blue-600" />
              {{ fr: 'Mot de passe réinitialisé', ru: 'Пароль сброшен', md: 'Parolă resetată', uk: 'Пароль скинуто' }[language] || 'Mot de passe réinitialisé'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-green-800 text-sm font-medium mb-2">
                {{ fr: 'Nouveau mot de passe pour', ru: 'Новый пароль для', md: 'Parolă nouă pentru', uk: 'Новий пароль для' }[language] || 'Nouveau mot de passe pour'} <strong>{resetPasswordDialog?.userName}</strong>
              </p>
              <p className="text-green-600 text-xs mb-3">{resetPasswordDialog?.userEmail}</p>
              
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-white border-2 border-green-300 rounded-lg p-3 font-mono text-lg font-bold text-center select-all">
                  {resetPasswordDialog?.temporaryPassword}
                </div>
                <Button
                  onClick={() => copyToClipboard(resetPasswordDialog?.temporaryPassword)}
                  variant="outline"
                  className={`h-12 px-4 ${copiedPassword ? 'bg-green-100 border-green-400' : ''}`}
                >
                  {copiedPassword ? <Check className="w-5 h-5 text-green-600" /> : <Copy className="w-5 h-5" />}
                </Button>
              </div>
            </div>
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-amber-800 text-sm">
                {{ 
                  fr: "⚠️ Communiquez ce mot de passe à l'employé par téléphone, SMS ou WhatsApp. Il pourra ensuite le modifier dans son profil.",
                  ru: "⚠️ Сообщите этот пароль сотруднику по телефону, SMS или WhatsApp. Он сможет изменить его в профиле.",
                  md: "⚠️ Comunicați această parolă angajatului prin telefon, SMS sau WhatsApp. El o poate schimba în profil.",
                  uk: "⚠️ Повідомте цей пароль співробітнику телефоном, SMS або WhatsApp. Він зможе змінити його в профілі."
                }[language] || "⚠️ Communiquez ce mot de passe à l'employé par téléphone, SMS ou WhatsApp. Il pourra ensuite le modifier dans son profil."}
              </p>
            </div>
            
            <Button 
              onClick={() => setResetPasswordDialog(null)} 
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              {{ fr: 'Compris', ru: 'Понятно', md: 'Am înțeles', uk: 'Зрозуміло' }[language] || 'Compris'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Custom Confirm Dialog */}
      <Dialog open={!!confirmDialog} onOpenChange={(open) => !open && setConfirmDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              {confirmDialog?.type === 'delete' ? (
                <Trash2 className="w-5 h-5 text-red-600" />
              ) : (
                <KeyRound className="w-5 h-5 text-amber-600" />
              )}
              {confirmDialog?.title}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-gray-600">{confirmDialog?.message}</p>
            <div className="flex gap-3">
              <Button 
                onClick={() => setConfirmDialog(null)} 
                variant="outline" 
                className="flex-1"
              >
                {{ fr: 'Annuler', ru: 'Отмена', md: 'Anulează', uk: 'Скасувати' }[language] || 'Annuler'}
              </Button>
              <Button 
                onClick={() => {
                  if (confirmDialog?.type === 'delete') {
                    confirmDeleteMember();
                  } else if (confirmDialog?.type === 'resetPassword') {
                    confirmResetPassword();
                  }
                }} 
                className={confirmDialog?.type === 'delete' ? 'flex-1 bg-red-600 hover:bg-red-700' : 'flex-1 bg-amber-600 hover:bg-amber-700'}
              >
                {{ fr: 'Confirmer', ru: 'Подтвердить', md: 'Confirmă', uk: 'Підтвердити' }[language] || 'Confirmer'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Fullscreen avatar viewer */}
      {photoPreview && (
        <div
          className="fixed inset-0 bg-black/90 flex flex-col items-center justify-center z-[99999] p-4"
          onClick={() => setPhotoPreview(null)}
          data-testid="avatar-preview-overlay"
        >
          <button onClick={() => setPhotoPreview(null)} className="absolute top-4 right-4 text-white">
            <X className="w-8 h-8" />
          </button>
          <img src={photoPreview.url} alt={photoPreview.name} className="max-w-full max-h-[80vh] object-contain rounded-2xl shadow-2xl" />
          <p className="text-white font-semibold mt-4 text-lg">{photoPreview.name}</p>
        </div>
      )}

      {/* Founder Section */}
      <div className="card-base p-6 bg-gradient-to-r from-[#0E2147] to-[#1a3a6e]">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex flex-col sm:flex-row items-center gap-6">
            <div
              className="w-24 h-24 rounded-full overflow-hidden border-4 border-white/20 shadow-xl flex-shrink-0 cursor-pointer active:scale-95 transition-transform"
              onClick={() => setPhotoPreview({ url: '/founder.jpg', name: 'M. Savitch Pavel' })}
            >
              <img
                src="/founder.jpg"
                alt={{ fr: 'Fondateur', ru: 'Основатель', md: 'Fondator', uk: 'Засновник' }[language] || 'Fondateur'} 
                className="w-full h-full object-cover"
              />
            </div>
            <div className="text-center sm:text-left">
              <p className="text-white/60 text-sm uppercase tracking-wider font-semibold mb-1">
                {{ fr: 'Fondateur', ru: 'Основатель', md: 'Fondator', uk: 'Засновник' }[language] || 'Fondateur'}
              </p>
              <h3 className="text-xl font-bold text-white">
                M. Savitch Pavel
              </h3>
              <p className="text-white/80 text-sm mt-1">
                La Piscine Container
              </p>
              <p className="text-white/80 text-sm">
                Eco Bati Construction
              </p>
            </div>
          </div>
          <div className="flex-shrink-0">
            <img src="/logo-piscine-white.png" alt="Logo" className="w-24 h-24 object-contain" />
          </div>
        </div>
      </div>
    </div>
  );
}
